import {
  faArrowUpRightFromSquare,
  faCheck,
  faCircleCheck,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import AppText from "components/AppText";

type LinkBridgeSuccessProps = {
  platform: "meta" | "google";
  accountName?: string;
  accountId?: string;
  managerName?: string;
  managerId?: string;
  viewAssetsHref?: string;
  message?: string;
};

const LinkBridgeSuccess = ({
  platform,
  accountName,
  accountId,
  managerName,
  managerId,
  viewAssetsHref,
  message,
}: LinkBridgeSuccessProps) => {
  const isMeta = platform === "meta";
  const logoSrc = isMeta
    ? "images/global/meta.png"
    : "images/global/google-icon.png";
  const platformLabel = isMeta ? "Meta" : "Google";

  return (
    <div className="px-1 pb-4 pt-2 sm:px-2">
      <div className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-b from-success-soft/80 to-surface p-6 sm:p-8">
        <div
          className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-success/10 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 -left-8 h-36 w-36 rounded-full bg-primary/10 blur-2xl"
          aria-hidden
        />

        <div className="relative flex flex-col items-center text-center">
          <div className="relative mb-5 flex h-20 w-20 items-center justify-center">
            <span
              className="absolute inset-0 rounded-full bg-success/20 animate-lb-ring"
              aria-hidden
            />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success shadow-soft animate-lb-pop">
              <FontAwesomeIcon
                icon={faCircleCheck as IconProp}
                className="text-[34px]"
              />
            </div>
          </div>

          <p className="m-0 mb-2 text-[1.45rem] font-bold tracking-[-0.03em] text-ink animate-lb-fade-up">
            <AppText>You're connected</AppText>
          </p>
          <p className="m-0 max-w-md text-[0.95rem] leading-relaxed text-ink-secondary animate-lb-fade-up-delay">
            <AppText>
              {isMeta
                ? "Partner access was granted successfully. Your selected assets are now shared with the requesting Business Manager."
                : "Manager access was granted successfully. Your selected Google Ads account is now linked."}
            </AppText>
          </p>
        </div>

        <div className="relative mt-7 grid gap-3 animate-lb-fade-up-delay-2">
          <div className="rounded-xl border border-line bg-surface/90 p-4 text-left shadow-sm backdrop-blur-sm">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-muted">
                <img
                  src={logoSrc}
                  alt=""
                  className="h-6 w-6 object-contain"
                />
              </div>
              <div className="min-w-0">
                <p className="m-0 text-[0.72rem] font-bold uppercase tracking-[0.06em] text-ink-muted">
                  <AppText>Linked ad account</AppText>
                </p>
                <p className="m-0 truncate text-[1rem] font-bold text-ink">
                  {accountName || "—"}
                </p>
              </div>
            </div>
            {accountId ? (
              <p className="m-0 rounded-lg bg-muted px-3 py-2 text-[0.82rem] text-ink-secondary">
                <span className="font-semibold text-ink">
                  <AppText>Account ID</AppText>:
                </span>{" "}
                {accountId}
              </p>
            ) : null}
          </div>

          <div className="rounded-xl border border-line bg-surface/90 p-4 text-left shadow-sm">
            <p className="m-0 mb-3 text-[0.72rem] font-bold uppercase tracking-[0.06em] text-ink-muted">
              <AppText>Access summary</AppText>
            </p>
            <div className="grid gap-2.5 sm:grid-cols-2">
              <div className="rounded-lg bg-muted px-3 py-2.5">
                <p className="m-0 mb-1 text-[0.72rem] font-semibold text-ink-muted">
                  <AppText>{platformLabel} manager</AppText>
                </p>
                <p className="m-0 break-words text-[0.9rem] font-semibold text-ink">
                  {managerName || "—"}
                  {managerId ? (
                    <span className="font-normal text-ink-secondary">
                      {" "}
                      ({managerId})
                    </span>
                  ) : null}
                </p>
              </div>
              <div className="rounded-lg bg-muted px-3 py-2.5">
                <p className="m-0 mb-1 text-[0.72rem] font-semibold text-ink-muted">
                  <AppText>Access level</AppText>
                </p>
                <p className="m-0 inline-flex items-center gap-1.5 text-[0.9rem] font-semibold text-ink">
                  <FontAwesomeIcon
                    icon={faShieldHalved as IconProp}
                    className="text-primary-text"
                  />
                  <AppText>Manage</AppText>
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-line bg-muted/70 px-4 py-3 text-left">
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {[
                "Connection completed securely",
                isMeta
                  ? "Assets are visible to the partner Business Manager"
                  : "Your Google Ads account is ready for campaigns",
                "You can review or revoke access later",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 text-[0.86rem] leading-snug text-ink-secondary"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success-soft text-success">
                    <FontAwesomeIcon
                      icon={faCheck as IconProp}
                      className="text-[10px]"
                    />
                  </span>
                  <AppText>{item}</AppText>
                </li>
              ))}
            </ul>
          </div>

          {message ? (
            <div className="rounded-xl border border-success/20 bg-success-soft px-4 py-3 text-[0.88rem] font-semibold text-success">
              {message}
            </div>
          ) : null}

          {viewAssetsHref ? (
            <a
              href={viewAssetsHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-primary px-5 text-[0.95rem] font-semibold text-white no-underline shadow-btn transition hover:bg-primary-hover"
            >
              <AppText>View shared assets</AppText>
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare as IconProp}
                className="text-[12px]"
              />
            </a>
          ) : (
            <div className="inline-flex h-12 w-full items-center justify-center rounded-[10px] border border-line bg-surface px-5 text-[0.95rem] font-semibold text-ink">
              <AppText>You can safely close this window</AppText>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkBridgeSuccess;
